// ФТУЕ.3: скриптованный туториал-бой (визуальная симуляция).
//
// - Поле 4 линии (тыл-бота / 1-я бота / 1-я игрока / тыл-игрока) — по диздоку §6.1
// - Карты в руке — реальные нейтральные через GameCard variant="compact"
// - Юниты на поле — реальные карточки через getCardData
// - Простые CSS-анимации атак между шагами (shake, fade-out)
//
// Сценарий — чистый React state (без engine). Сделано так чтобы гарантированно
// работало в TG WebView. Реальный движок подключим в полировке.

import { useEffect, useRef, useState } from 'react';
import { useTelegram } from '../telegram/useTelegram';
import { useTr } from '../i18n/useTr';
import { GameCard } from '../components/GameCard';
import { getCardData } from '../lib/cardData';
import s from './TutorialBattle.module.css';

// Минимальное расстояние в px чтобы pointer-move стал считаться drag'ом.
const DRAG_THRESHOLD = 6;

// SVG-иконки — порт из BattleScreen.tsx (БОЙ.1.7). Через currentColor
// перекрашиваются в фракционные цвета чипов.
function IcoAtk() {
  return <svg viewBox="0 0 16 16" width="9" height="9" fill="currentColor">
    <path d="M3 13l1 0 8-8 0-2-1-1-2 0-8 8 0 1zm9-12l1 1-1 1-1-1zM2 14l4 0 0 1-4 0z" />
  </svg>;
}
function IcoArm() {
  return <svg viewBox="0 0 16 16" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M8 1 L14 4 V8 C14 11.5 11 14 8 15 C5 14 2 11.5 2 8 V4 Z" />
  </svg>;
}
function IcoHp() {
  return <svg viewBox="0 0 16 16" width="9" height="9" fill="currentColor">
    <path d="M8 14s-5-3.5-5-7.5C3 4 4.5 3 6 3c1 0 1.5.5 2 1.5C8.5 3.5 9 3 10 3c1.5 0 3 1 3 3.5 0 4-5 7.5-5 7.5z" />
  </svg>;
}
function IcoDeck() {
  return <svg viewBox="0 0 16 16" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.4">
    <rect x="3" y="4" width="9" height="11" rx="1" />
    <path d="M5 2 H14 V13" />
  </svg>;
}
function IcoDiscard() {
  return <svg viewBox="0 0 16 16" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.4">
    <rect x="3" y="4" width="9" height="11" rx="1" />
    <line x1="5" y1="9" x2="10" y2="9" />
  </svg>;
}

// Боевая анимация — порт из app/src/animation/AttackOverlay (БОЙ.1.4):
// ghost-копия атакующего летит по дуге (telegraph→lunge→impact→return),
// 3 motion-trail копии плывут следом, на цели вспыхивает impact-flash и
// shake-overlay, из цели вылетают damage-numbers веером.
interface AtkAnim {
  id: number;
  fromSel: string;   // CSS-селектор атакующего
  toSel: string;     // селектор цели
  label?: string;    // текст ghost'а (имя юнита/Героя)
  color?: 'red' | 'cyan';
  damage?: number;   // урон для damage-numbers; 0/undefined = без чисел
}

interface TutorialBattleProps {
  onWin: () => void;
}

type Step =
  | 'intro'
  | 'pick-card'      // drag карта в руке → линия
  | 'placed'         // карта на поле, жми Дальше
  | 'bot-placed'     // бот выложил свою, жми Дальше
  | 'reveal'         // ход 1 Ф3 авто-атаки (Чумба ↔ Контрабандист)
  | 'reveal-done'    // итог Ф3
  | 'hero-attack'    // ход 1 Ф4: tap Контрабандиста (добить)
  | 'unit-killed'    // юнит бота убит
  | 'bot-turn'       // ход 1 Ф5: бот скип Героя (Кочевники медлят)
  | 'turn2-intro'    // ход 2, жми Дальше
  | 'turn2-pick'     // ход 2 Ф2: drag Бугая на линию
  | 'turn2-reveal'   // ход 2 Ф3 авто-бой Чумба+Бугай → Hero бота
  | 'turn2-reveal-done' // итог Ф3 хода 2
  | 'turn2-hero'     // ход 2 Ф4: tap Героя бота (добить)
  | 'victory';

// COACH-тексты генерируются с учётом локали через useTr.
// На RU — оригинал; на EN — найт-сити-стиль (choom, Wastelander Nomads etc.).
type CoachText = { title: string; body: string };
function buildCoach(t: (ru: string, en: string) => string): Record<Step, CoachText> {
  return {
    intro:        { title: t('Первый бой', 'First Fight'),                body: t('Это Кочевники Пустошей. У них слабый Герой — обнули его за пару ходов. Бой идёт по фазам: 1 тяга → 2 размещение → 3 авто-бой → 4-5 атаки Героев.', "These are Wasteland Nomads. Their Hero is weak — zero him out in a couple of turns. Battle has phases: 1 draw → 2 placement → 3 auto-combat → 4-5 Hero attacks.") },
    'pick-card':  { title: t('Ф2 · Размещение', 'Phase 2 · Placement'),   body: t('Карты в руке. Тап — посмотреть детали карты. Чтобы разместить — зажми «Чумбу с трубой» (она пульсирует) и перетащи на свою 1-ю линию. У тебя 2 линии: 1-я (передовая) и 2-я (тыл) — ставь куда хочешь, тактика твоя.', "Cards in hand. Tap — see card details. To place — long-press «Choom with Pipe» (it pulses) and drag onto your 1st line. You have 2 lines: 1st (front) and 2nd (back) — place wherever, tactics are yours.") },
    placed:       { title: t('Карта размещена', 'Card Placed'),           body: t('«Чумба с трубой» встал на твою передовую. Жди ход противника. Жми «Дальше».', "«Choom with Pipe» is on your front line. Wait for opponent's turn. Hit «Next».") },
    'bot-placed': { title: t('Ход противника', "Opponent's Turn"),        body: t('Кочевники выложили Контрабандиста (2 атаки / 3 хп) — крепкий парень. Сейчас Ф3 — вскрытие и авто-атаки.', "The Nomads dropped a Drone Smuggler (2 atk / 3 hp) — a tough one. Now it's Phase 3 — reveal and auto-attacks.") },
    reveal:       { title: t('Ф3 · Вскрытие', 'Phase 3 · Reveal'),        body: t('Юниты бьют друг друга автоматически по приказу своей корпы — ты не управляешь авто-боем. Смотри!', "Units hit each other automatically by their corp's orders — you don't control the auto-combat. Watch!") },
    'reveal-done':{ title: t('Урон нанесён', 'Damage Dealt'),             body: t('Чумба ударил Контрабандиста на 2, Контрабандист ответил на 2. Оба выжили с 1 HP. У Героя бота 6 HP (Чумба тоже била по линии — напротив был Контрабандист).', "Choom hit Smuggler for 2, Smuggler hit back for 2. Both survived at 1 HP. Bot Hero has 6 HP (Choom hit along the lane — the Smuggler was opposite).") },
    'hero-attack':{ title: t('Ф4 · Атака Героем', 'Phase 4 · Hero Attack'), body: t('Твой ход. Тапни Контрабандиста чтобы добить его. Героя пока бить нельзя — у врага есть юнит на поле (правило «Авангард»).', "Your move. Tap the Smuggler to finish him off. Can't hit the Hero yet — enemy has a unit on the field (Hero Cover rule).") },
    'unit-killed':{ title: t('Юнит уничтожен', 'Unit Destroyed'),         body: t('Контрабандист убит. Поле Кочевников пустое — теперь их Герой открыт. Жми «Дальше».', "Smuggler is dead. The Nomads' field is empty — their Hero is exposed. Hit «Next».") },
    'bot-turn':   { title: t('Ф5 · Ход Кочевников', "Phase 5 · Nomads' Turn"), body: t('Их Герой пропустил атаку — у Кочевников плохая тактика. Чумба выжила с 1 HP. Жми «Дальше».', "Their Hero skipped the attack — Nomads have bad tactics. Choom survived at 1 HP. Hit «Next».") },
    'turn2-intro':{ title: t('Ход 2', 'Turn 2'),                          body: t('Ты добираешь карту. У Кочевников 6 HP, поле пусто. Время добивать.', "You draw a card. Nomads have 6 HP, field is empty. Time to finish them.") },
    'turn2-pick': { title: t('Ход 2 · Ф2', 'Turn 2 · Phase 2'),           body: t('Перетащи Бугая со шкворнем (3 атк / 3 хп) на свою 1-ю линию. Чумба тоже ударит — суммарно 5 атаки в Героя бота.', "Drag Thug with Lugnut (3 atk / 3 hp) onto your 1st line. Choom will hit too — 5 total damage to the bot Hero.") },
    'turn2-reveal':     { title: t('Ход 2 · Ф3', 'Turn 2 · Phase 3'),     body: t('Авто-бой: Чумба (2 атк) + Бугай (3 атк) бьют по линии. У Кочевников нет юнитов — обе атаки летят в Героя. 6 HP − 5 = 1 HP.', "Auto-combat: Choom (2 atk) + Thug (3 atk) hit along the lane. Nomads have no units — both attacks land on the Hero. 6 HP − 5 = 1 HP.") },
    'turn2-reveal-done':{ title: t('Герой почти мёртв', 'Hero Almost Dead'), body: t('У Героя Кочевников 1 HP. Жми «Дальше», и добей Лидером.', "Nomads' Hero is at 1 HP. Hit «Next» and finish him with the Leader.") },
    'turn2-hero': { title: t('Добивай!', 'Finish Him!'),                  body: t('Тапни Героя Кочевников сверху — твой Лидер нанесёт 1 урон. 1 − 1 = 0. Победа.', "Tap the Nomads' Hero up top — your Leader deals 1 damage. 1 − 1 = 0. Victory.") },
    victory:      { title: t('Победа!', 'Victory!'),                      body: t('Ты обнулил Героя Кочевников. Цель любого боя — именно это. Дальше — твои награды.', "You zeroed out the Nomads' Hero. That's the goal of every battle. Next — your rewards.") }
  };
}

// Рука хода 1: Чумба с трубой ПЕРВАЯ — её мы и просим разместить в подсказке.
const PLAYER_HAND_T1_IDS = [
  'neutral_chumba_truba',
  'neutral_drone_mukha',
  'neutral_kontrabandist_dronov',
  'neutral_bugay_so_shkvornem'
];

// Рука хода 2: Бугай со шкворнем ПЕРВЫЙ — его и размещаем для добивания.
const PLAYER_HAND_T2_IDS = [
  'neutral_bugay_so_shkvornem',
  'neutral_chumba_truba',
  'neutral_kontrabandist_dronov'
];

// Юниты на поле в разные шаги — id'ы реальных нейтральных карт.
// В скобках — какой атк/хп показывать (мог отличаться от карты из-за «полученного урона»).
type FieldUnit = { id: string; atkOverride?: number; hpOverride?: number; dying?: boolean };

export function TutorialBattle({ onWin }: TutorialBattleProps) {
  const { hapticTap, hapticSuccess } = useTelegram();
  const t = useTr();
  const COACH = buildCoach(t);
  const [step, setStep] = useState<Step>('intro');
  const [atks, setAtks] = useState<AtkAnim[]>([]);

  // Drag&drop состояние. Карта в руке начинает drag через pointerdown,
  // активируется когда указатель ушёл > DRAG_THRESHOLD px от старта.
  // Если drag не активировался (короткий тап) — открываем полную карту.
  const [drag, setDrag] = useState<{
    cardId: string;
    x: number; y: number;       // текущая позиция указателя
    startX: number; startY: number;
    activated: boolean;
    hoverDrop: boolean;          // курсор над drop-zone (линия игрока)
  } | null>(null);
  const dragRef = useRef(drag);
  dragRef.current = drag;

  // Модалка деталей карты (по тапу без drag).
  const [detailCardId, setDetailCardId] = useState<string | null>(null);

  // Запускает 1 или несколько ghost-анимаций атак.
  // atk-full 900ms + dmg-fly 700ms + delay 460ms — итого ~1200ms.
  // Через 1400ms убираем все элементы из DOM (чтобы dmg успели улететь).
  function fireAttacks(list: Omit<AtkAnim, 'id'>[]) {
    const id0 = Date.now();
    const items: AtkAnim[] = list.map((x, i) => ({ ...x, id: id0 + i }));
    setAtks(prev => [...prev, ...items]);
    setTimeout(() => {
      setAtks(prev => prev.filter(a => !items.find(it => it.id === a.id)));
    }, 1400);
  }

  // При смене step — запускаем нужные атаки.
  useEffect(() => {
    if (step === 'reveal') {
      // Авто-бой: твой юнит бьёт юнита бота И юнит бота бьёт твоего.
      // Чумба (2 атк) → Контрабандист (3 хп → 1 хп)
      // Контрабандист (2 атк) → Чумба (3 хп → 1 хп)
      fireAttacks([
        { fromSel: '[data-tut="me-1-0"]', toSel: '[data-tut="bot-1-0"]', label: t('«Чумба»', '«Choom»'), color: 'cyan', damage: 2 },
        { fromSel: '[data-tut="bot-1-0"]', toSel: '[data-tut="me-1-0"]', label: t('Контрабандист', 'Smuggler'), color: 'red', damage: 2 }
      ]);
    } else if (step === 'unit-killed') {
      fireAttacks([
        { fromSel: '[data-tut="me-hero"]', toSel: '[data-tut="bot-1-0"]', label: t('Лидер', 'Leader'), color: 'cyan', damage: 1 }
      ]);
    } else if (step === 'bot-turn') {
      // Бот скипает атаку Героя.
    } else if (step === 'turn2-reveal') {
      fireAttacks([
        { fromSel: '[data-tut="me-1-0"]', toSel: '[data-tut="bot-hero"]', label: t('«Чумба»', '«Choom»'), color: 'cyan', damage: 2 },
        { fromSel: '[data-tut="me-1-1"]', toSel: '[data-tut="bot-hero"]', label: t('Бугай', 'Thug'), color: 'cyan', damage: 3 }
      ]);
    } else if (step === 'victory') {
      fireAttacks([
        { fromSel: '[data-tut="me-hero"]', toSel: '[data-tut="bot-hero"]', label: t('Лидер', 'Leader'), color: 'cyan', damage: 1 }
      ]);
    }
  }, [step]);

  const isT2 = step === 'turn2-intro' || step === 'turn2-pick'
            || step === 'turn2-reveal' || step === 'turn2-reveal-done'
            || step === 'turn2-hero'  || step === 'victory';

  // Полные статы Героев (как в реальном бою): ATK / ARM / HP.
  // Нейтрал-«Лидер улицы» по диздоку — слабый Герой (1 атк, 0 брони, 25 хп).
  // «Глава клана» (нейтрал-бот) — то же, но 1 атк / 0 брони / 8 хп (туториал).
  const playerHero = {
    name: t('Нейтрал · Лидер', 'Neutral · Leader'),
    atk: 1,
    armor: 0,
    hp: step === 'bot-turn' || step === 'turn2-intro' || step === 'turn2-pick' || step === 'turn2-hero' || step === 'victory' ? 24 : 25
  };
  // Hero HP бота по шагам (точная арифметика):
  // 8 → -2 (Чумба бьёт по линии, напротив Контрабандист, но юнит-атаки
  //         в этой механике переходят на Героя если враг 0/0 — поэтому Чумба
  //         наносит 2 урона Контрабандисту, а не Герою). Hero бота остаётся 8.
  // Hmm, по диздоку §6.5: юнит бьёт ПО ЛИНИИ — если есть юнит напротив, его;
  // иначе Героя. На Ход 1 Ф3 Контрабандист стоит напротив Чумбы — оба бьют
  // друг друга. Hero бота 8 → 8 (никто не бил).
  // Корректировка: Hero бота 8 на всех шагах хода 1, кроме победы.
  // Ход 2: Чумба + Бугай → Hero (8 → 3 → 0 = НЕТ, нужно за 2 удара).
  // Решение: Hero бота с 5 HP (меньше). Тогда:
  //   ход 2 Ф3: 5 - 2 (Чумба) - 3 (Бугай) = 0 — добил без Героя.
  // Чтобы Лидер участвовал в добивании — Hero бота 6 HP, после Ф3 = 1.
  //   ход 2 Ф4 Лидер 1 атк → 1 - 1 = 0. ✓
  // Ставим стартовое Hero бота 6 (не 8 как раньше).
  const botHpByStep: Record<Step, number> = {
    intro: 6, 'pick-card': 6, placed: 6, 'bot-placed': 6, reveal: 6,
    'reveal-done': 6, 'hero-attack': 6, 'unit-killed': 6, 'bot-turn': 6,
    'turn2-intro': 6, 'turn2-pick': 6,
    'turn2-reveal': 6, 'turn2-reveal-done': 1, 'turn2-hero': 1,
    victory: 0
  };
  const botHero = { name: t('Глава клана', 'Clan Chief'), atk: 1, armor: 0, hp: botHpByStep[step] };

  // Стопка колоды / сброса — для индикаторов как в реальном бою.
  // У игрока в руке 4 (ход 1) или 3 (ход 2). Деки разные.
  const myHand = isT2 ? 3 : 4;
  const myDeck = isT2 ? 16 : 19;
  const myDisc = isT2 ? 1 : 0;
  const botHand = isT2 ? 3 : 4;
  const botDeck = isT2 ? 16 : 19;
  const botDisc = isT2 ? 1 : 0;

  // Текущая фаза + ход — для центрального war-band.
  const turnNum = isT2 ? 2 : 1;
  const phaseInfo: Record<Step, { num: string; label: string }> = {
    intro:                { num: '—', label: t('начало боя', 'battle start') },
    'pick-card':          { num: '2', label: t('размещение', 'placement') },
    placed:               { num: '2', label: t('размещение', 'placement') },
    'bot-placed':         { num: '2', label: t('размещение', 'placement') },
    reveal:               { num: '3', label: t('вскрытие', 'reveal') },
    'reveal-done':        { num: '3', label: t('вскрытие', 'reveal') },
    'hero-attack':        { num: '4', label: t('герой 1', 'hero 1') },
    'unit-killed':        { num: '4', label: t('герой 1', 'hero 1') },
    'bot-turn':           { num: '5', label: t('герой 2', 'hero 2') },
    'turn2-intro':        { num: '1', label: t('тяга', 'draw') },
    'turn2-pick':         { num: '2', label: t('размещение', 'placement') },
    'turn2-reveal':       { num: '3', label: t('вскрытие', 'reveal') },
    'turn2-reveal-done':  { num: '3', label: t('вскрытие', 'reveal') },
    'turn2-hero':         { num: '4', label: t('герой 1', 'hero 1') },
    victory:              { num: '✓', label: t('победа', 'victory') }
  };
  const ph = phaseInfo[step];

  const handIds = isT2 ? PLAYER_HAND_T2_IDS : PLAYER_HAND_T1_IDS;
  const showHand = step === 'pick-card' || step === 'turn2-pick';

  // Поле игрока — передовая (линия 1).
  // Чумба ставится на 'placed' и держится до конца боя (с убывающим HP).
  // На turn2-pick — Чумба ещё на месте (не пропадает!), Бугая ставит после drop.
  let playerLine1: (FieldUnit | null)[] = [null, null, null, null];
  if (isT2) {
    // Ход 2: Чумба сильно ранена (1 HP, после Ф3 хода 1). Бугай появляется
    // ПОСЛЕ drop на turn2-pick — то есть на turn2-reveal и далее.
    const placedBugay = step === 'turn2-reveal' || step === 'turn2-reveal-done'
                     || step === 'turn2-hero' || step === 'victory';
    playerLine1 = [
      { id: 'neutral_chumba_truba', hpOverride: 1 },
      placedBugay ? { id: 'neutral_bugay_so_shkvornem' } : null,
      null, null
    ];
  } else if (step === 'placed' || step === 'bot-placed' || step === 'reveal'
        || step === 'reveal-done' || step === 'hero-attack'
        || step === 'unit-killed' || step === 'bot-turn') {
    // Ход 1: Чумба на месте, HP убывает.
    //   placed/bot-placed/reveal — Чумба full (3 hp)
    //   reveal-done/hero-attack/unit-killed/bot-turn — 1 hp (после ответа Контрабандиста)
    //   bot-turn — Кочевники СКИПают атаку, Чумба остаётся с 1 hp (не умирает).
    const hp =
      step === 'placed' || step === 'bot-placed' || step === 'reveal' ? 3 : 1;
    playerLine1 = [{ id: 'neutral_chumba_truba', hpOverride: hp }, null, null, null];
  }

  // Поле бота — передовая (линия 1). Контрабандист (2/3) — крепкий.
  // После Ф3 (reveal-done): 3 → 1 хп. На hero-attack игрок добивает Героем (1 атка) → 0.
  let botLine1: (FieldUnit | null)[] = [null, null, null, null];
  if (step === 'bot-placed') {
    botLine1 = [{ id: 'neutral_kontrabandist_dronov', hpOverride: 3 }, null, null, null];
  } else if (step === 'reveal') {
    botLine1 = [{ id: 'neutral_kontrabandist_dronov', hpOverride: 3 }, null, null, null];
  } else if (step === 'reveal-done') {
    botLine1 = [{ id: 'neutral_kontrabandist_dronov', hpOverride: 1 }, null, null, null];
  } else if (step === 'hero-attack') {
    botLine1 = [{ id: 'neutral_kontrabandist_dronov', hpOverride: 1 }, null, null, null];
  }

  // Тыловые линии (2-я линия) — пустые в туториале по сценарию.
  const playerLine2: (FieldUnit | null)[] = [null, null, null, null];
  const botLine2: (FieldUnit | null)[] = [null, null, null, null];

  // Размещение карты (вызывается после успешного drop на свою линию).
  const onPlaceCard = () => {
    hapticSuccess();
    if (step === 'pick-card') setStep('placed');
    else if (step === 'turn2-pick') setStep('turn2-reveal'); // → авто-бой Ф3 хода 2
  };

  // Pointer-events для drag&drop. Listeners на window — чтобы drag
  // продолжался даже когда курсор уехал с карты.
  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      const dist = Math.hypot(dx, dy);
      const activated = d.activated || dist > DRAG_THRESHOLD;
      // Проверка drop-zone — только над 1-й линией ТЕКУЩЕГО игрока.
      let hoverDrop = false;
      if (activated) {
        const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
        if (el?.closest('[data-drop="me-1"]')) hoverDrop = true;
      }
      setDrag({ ...d, x: e.clientX, y: e.clientY, activated, hoverDrop });
    };
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.activated && d.hoverDrop) {
        // Успешный drop на свою линию.
        onPlaceCard();
      } else if (!d.activated) {
        // Короткий тап без движения — открыть полную карту в модалке.
        hapticTap();
        setDetailCardId(d.cardId);
      }
      // Сбрасываем pointer-capture (если был) и состояние drag.
      try { (e.target as HTMLElement)?.releasePointerCapture?.(e.pointerId); } catch { /* */ }
      setDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag !== null]);

  const onCardPointerDown = (cardId: string, e: React.PointerEvent) => {
    // Игнорируем правый клик / клавиатуру.
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    // Drag активен только когда coach ждёт размещения.
    if (step !== 'pick-card' && step !== 'turn2-pick') return;
    setDrag({
      cardId,
      x: e.clientX, y: e.clientY,
      startX: e.clientX, startY: e.clientY,
      activated: false,
      hoverDrop: false
    });
  };

  const canTapUnit = step === 'hero-attack';
  const canTapHero = step === 'turn2-hero';

  const onTapBotUnit = () => {
    hapticTap();
    if (step === 'hero-attack') setStep('unit-killed');
  };

  const onTapBotHero = () => {
    hapticTap();
    if (step === 'turn2-hero') {
      hapticSuccess();
      setStep('victory');
    }
  };

  const onPrimary = () => {
    hapticTap();
    if (step === 'victory') {
      onWin();
      return;
    }
    const NEXT: Partial<Record<Step, Step>> = {
      intro:              'pick-card',
      placed:             'bot-placed',
      'bot-placed':       'reveal',
      reveal:             'reveal-done',
      'reveal-done':      'hero-attack',
      'unit-killed':      'bot-turn',
      'bot-turn':         'turn2-intro',
      'turn2-intro':      'turn2-pick',
      'turn2-reveal':     'turn2-reveal-done',
      'turn2-reveal-done':'turn2-hero'
    };
    const next = NEXT[step];
    if (next) setStep(next);
  };

  const waitForTap = step === 'pick-card' || step === 'hero-attack'
                  || step === 'turn2-pick' || step === 'turn2-hero';
  const primaryLabel = step === 'victory' ? t('К НАГРАДАМ →', 'TO REWARDS →') : t('ДАЛЬШЕ →', 'NEXT →');

  // CSS-класс для анимации revealcombat
  const revealAnim = step === 'reveal' ? s.revealAnim : '';

  return (
    <div className={s.host}>
      {/* === Бот сверху === Вся pbar кликабельна на шаге turn2-hero. */}
      <div
        className={`${s.pbar} ${s.pbarEnemy} ${canTapHero ? s.pbarTargetable : ''} ${step === 'victory' ? s.pbarDead : ''}`}
        data-tut="bot-hero"
        onClick={canTapHero ? onTapBotHero : undefined}
      >
        <div className={s.av}>KK</div>
        <div className={s.pbarInfo}>
          <div className={s.pbarHeroName}>{botHero.name}</div>
          <div className={s.pbarSub}>Кочевники · ИИ</div>
        </div>
        <div className={s.pbarStatsCol}>
          <div className={s.pbarStatsRow}>
            <span className={`${s.statChip} ${s.chipAtk}`} title="Атака"><IcoAtk />{botHero.atk}</span>
            <span className={`${s.statChip} ${s.chipArm}`} title="Броня"><IcoArm />{botHero.armor}</span>
            <span className={`${s.statChip} ${s.chipHp}`} title="HP">
              <IcoHp />{botHero.hp}
            </span>
          </div>
          <div className={s.pbarStatsRow}>
            <span className={s.statChip} title="В руке">✋ {botHand}</span>
            <span className={s.statChip} title="В колоде"><IcoDeck />{botDeck}</span>
            <span className={s.statChip} title="В сбросе"><IcoDiscard />{botDisc}</span>
          </div>
        </div>
      </div>

      {/* === War-band (как в реальном бою, BattleScreen.warBand):
          Лево: сторона "первый ход" с точкой
          Центр: ХОД N + Ф{X} ФАЗА
          Право: placeholder ВК (Война Корпораций) === */}
      <div className={s.warBand}>
        <div className={s.wbSide}>
          <span className={s.wbDot} style={{ ['--fc' as any]: 'var(--neon-rd, #FF3D3D)' }} />
          <span className={s.wbFirstLabel}>{t('ПЕРВЫЙ', 'FIRST')}</span>
          <span className={`${s.wbFirstName} ${s.wbFirstEnemy}`}>{t('Бот', 'Bot')}</span>
        </div>
        <div className={s.wbCenter}>
          <span className={s.wbTurn}>{t('Ход', 'Turn')} {turnNum}</span>
          <span className={s.wbPhase}>{t('Ф', 'P')}{ph.num} · {ph.label.toUpperCase()}</span>
        </div>
        <div className={`${s.wbSide} ${s.wbSideRight}`}>
          <span className={s.wbWarPlaceholder}>{t('— ВК —', '— CW —')}</span>
        </div>
      </div>

      {/* === Поле (4 линии) === */}
      <div className={s.field}>
        <LaneRow units={botLine2}  side="bot" label={t('2 ЛИНИЯ', '2 LINE')} canTap={false} animOnReveal={revealAnim} onTap={onTapBotUnit} />
        <LaneRow units={botLine1}  side="bot" label={t('1 ЛИНИЯ', '1 LINE')} canTap={canTapUnit} animOnReveal={revealAnim} onTap={onTapBotUnit} />
        <LaneRow units={playerLine1} side="me" label={t('1 ЛИНИЯ', '1 LINE')} canTap={false} animOnReveal={revealAnim} onTap={() => {}}
                 isDropZone={showHand} hoverDrop={drag?.hoverDrop ?? false} dropAttr="me-1" />
        <LaneRow units={playerLine2} side="me" label={t('2 ЛИНИЯ', '2 LINE')} canTap={false} animOnReveal={revealAnim} onTap={() => {}} />
      </div>

      {/* === Игрок снизу === */}
      <div className={`${s.pbar} ${s.pbarMe}`} data-tut="me-hero">
        <div className={s.av}>{t('Я', 'ME')}</div>
        <div className={s.pbarInfo}>
          <div className={s.pbarHeroName}>{playerHero.name}</div>
          <div className={s.pbarSub}>{t('нейтрал · ты', 'neutral · you')}</div>
        </div>
        <div className={s.pbarStatsCol}>
          <div className={s.pbarStatsRow}>
            <span className={`${s.statChip} ${s.chipAtk}`} title="Атака"><IcoAtk />{playerHero.atk}</span>
            <span className={`${s.statChip} ${s.chipArm}`} title="Броня"><IcoArm />{playerHero.armor}</span>
            <span className={`${s.statChip} ${s.chipHp}`} title="HP"><IcoHp />{playerHero.hp}</span>
          </div>
          <div className={s.pbarStatsRow}>
            <span className={s.statChip} title="В руке">✋ {myHand}</span>
            <span className={s.statChip} title="В колоде"><IcoDeck />{myDeck}</span>
            <span className={s.statChip} title="В сбросе"><IcoDiscard />{myDisc}</span>
          </div>
        </div>
      </div>

      {/* === Рука (GameCard compact) ===
          Tap (без движения) — открыть полную карту в модалке.
          Drag на свою 1-ю линию — разместить юнита. */}
      {showHand && (
        <div className={s.hand}>
          {handIds.map((id, i) => {
            const card = getCardData(id);
            if (!card) return null;
            const isDragging = drag?.cardId === id && drag.activated;
            return (
              <div
                key={id + i}
                className={`${s.hcardWrap} ${i === 0 ? s.hcardWrapPulse : ''} ${isDragging ? s.hcardWrapDragging : ''}`}
                onPointerDown={(e) => onCardPointerDown(id, e)}
                style={{ touchAction: 'none' }}
              >
                <GameCard card={card} variant="compact" />
              </div>
            );
          })}
        </div>
      )}

      {/* === Drag-ghost: летит за курсором при активном drag === */}
      {drag && drag.activated && (() => {
        const card = getCardData(drag.cardId);
        if (!card) return null;
        return (
          <div
            className={s.dragGhost}
            style={{ left: drag.x, top: drag.y }}
          >
            <GameCard card={card} variant="compact" />
          </div>
        );
      })()}

      {/* === Модалка деталей карты (открывается тапом без drag) === */}
      {detailCardId && (() => {
        const card = getCardData(detailCardId);
        if (!card) return null;
        return (
          <div className={s.detailModal} onClick={() => setDetailCardId(null)}>
            <div className={s.detailInner} onClick={(e) => e.stopPropagation()}>
              <GameCard card={card} variant="full" />
              <button className={s.detailClose} onClick={() => setDetailCardId(null)}>
                {t('ЗАКРЫТЬ', 'CLOSE')}
              </button>
              <div className={s.detailHint}>
                {t('Чтобы разместить — закрой и ', 'To place — close and ')}<b>{t('перетащи', 'drag')}</b>{t(' карту на свою 1-ю линию.', ' the card onto your 1st line.')}
              </div>
            </div>
          </div>
        );
      })()}

      {/* === Атак-анимации (ghost-копия летит по дуге) === */}
      {atks.map(a => <AttackGhost key={a.id} anim={a} />)}

      {/* === Coach === */}
      <div className={s.coach}>
        <div className={s.coachKicker}>● {step === 'victory' ? t('ВИКТОРИЯ', 'VICTORY') : t('ПОДСКАЗКА', 'HINT')}</div>
        <div className={s.coachTitle}>{COACH[step].title}</div>
        <p className={s.coachBody}>{COACH[step].body}</p>
        {!waitForTap && (
          <button className={s.coachBtn} onClick={onPrimary}>
            {primaryLabel}
          </button>
        )}
        {waitForTap && (
          <div className={s.coachWaiting}>
            {step === 'hero-attack'
              ? t('↓ тапни юнита врага', '↓ tap the enemy unit')
              : step === 'turn2-hero'
                ? t('↑ тапни Героя бота сверху', '↑ tap the bot Hero above')
                : t('↑ перетащи карту на свою 1-ю линию', '↑ drag a card onto your 1st line')}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Полная анимация атаки — порт из app/src/animation/AttackOverlay.
//   1. ghost-копия атакующего летит по дуге (atk-full, 900ms)
//   2. 3 motion-trail копии плывут следом (delay 35/70/105ms)
//   3. На цели: impact-flash (radial gradient, 460ms delay)
//   4. На цели: shake-overlay (red border + smol shake, 460ms delay)
//   5. Damage-numbers — несколько чисел вылетают веером из цели
function AttackGhost({ anim }: { anim: AtkAnim }) {
  const [pos, setPos] = useState<{
    aLeft: number; aTop: number; aW: number; aH: number;
    tLeft: number; tTop: number; tW: number; tH: number;
    dx: number; dy: number;
  } | null>(null);

  useEffect(() => {
    const from = document.querySelector(anim.fromSel);
    const to   = document.querySelector(anim.toSel);
    if (!from || !to) return;
    const fr = from.getBoundingClientRect();
    const tr = to.getBoundingClientRect();
    const fcx = fr.left + fr.width / 2;
    const fcy = fr.top + fr.height / 2;
    const tcx = tr.left + tr.width / 2;
    const tcy = tr.top + tr.height / 2;
    setPos({
      aLeft: fr.left, aTop: fr.top, aW: fr.width, aH: fr.height,
      tLeft: tr.left, tTop: tr.top, tW: tr.width, tH: tr.height,
      dx: tcx - fcx,
      dy: tcy - fcy
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!pos) return null;

  const ghostStyle: React.CSSProperties = {
    left: pos.aLeft,
    top: pos.aTop,
    width: pos.aW,
    height: pos.aH,
    ['--lunge-dx' as any]: `${pos.dx}px`,
    ['--lunge-dy' as any]: `${pos.dy}px`
  };

  // Damage numbers — массив с разлётом веером.
  const dmg = anim.damage ?? 0;
  const numbers = dmg > 0 ? computeDamageFan(dmg) : [];

  return (
    <>
      {/* Motion-trail x3 — летят следом с delays. */}
      <div className={`${s.atkTrail} ${anim.color === 'red' ? s.atkTrailRed : s.atkTrailCyan} ${s.atkTrail1}`} style={ghostStyle} />
      <div className={`${s.atkTrail} ${anim.color === 'red' ? s.atkTrailRed : s.atkTrailCyan} ${s.atkTrail2}`} style={ghostStyle} />
      <div className={`${s.atkTrail} ${anim.color === 'red' ? s.atkTrailRed : s.atkTrailCyan} ${s.atkTrail3}`} style={ghostStyle} />

      {/* Главный ghost атакующего. */}
      <div
        className={`${s.atkGhost} ${anim.color === 'red' ? s.atkGhostRed : s.atkGhostCyan}`}
        style={ghostStyle}
      >
        {anim.label && <div className={s.atkGhostLabel}>{anim.label}</div>}
      </div>

      {/* Impact-flash и shake-overlay на цели (поверх target rect). */}
      <div
        className={s.atkFlash}
        style={{ left: pos.tLeft, top: pos.tTop, width: pos.tW, height: pos.tH }}
      />
      <div
        className={s.atkShake}
        style={{ left: pos.tLeft, top: pos.tTop, width: pos.tW, height: pos.tH }}
      />

      {/* Damage-numbers — вылетают из центра цели. */}
      {numbers.map((n, i) => (
        <div
          key={i}
          className={`${s.dmgNum} ${s.dmgHp}`}
          style={{
            left: pos.tLeft + pos.tW / 2,
            top: pos.tTop + pos.tH / 2,
            ['--dx' as any]: `${n.dx}px`,
            ['--dy' as any]: `${n.dy}px`,
            ['--rot' as any]: `${n.rot}deg`,
            ['--delay' as any]: `${460 + i * 80}ms`
          }}
        >
          −{n.value}
        </div>
      ))}
    </>
  );
}

// Разлёт damage-чисел веером.
function computeDamageFan(total: number): Array<{ value: number; dx: number; dy: number; rot: number }> {
  // Один большой номер по центру — для туториала достаточно.
  // (В реальном AttackOverlay их может быть несколько — мы делим урон).
  return [{
    value: total,
    dx: (Math.random() * 40 - 20),       // ±20px по горизонтали
    dy: -60 - Math.random() * 20,         // вверх на 60-80px
    rot: (Math.random() * 12 - 6)        // ±6° поворот
  }];
}

// ============================================================
function LaneRow({
  units, side, label, canTap, onTap, animOnReveal,
  isDropZone = false, hoverDrop = false, dropAttr
}: {
  units: (FieldUnit | null)[];
  side: 'bot' | 'me';
  label: string;
  canTap: boolean;
  onTap: (u: FieldUnit) => void;
  animOnReveal: string;
  /** Линия — валидная цель для drag&drop карты из руки. */
  isDropZone?: boolean;
  /** Курсор над линией прямо сейчас (для подсветки). */
  hoverDrop?: boolean;
  /** data-drop="me-1" для elementFromPoint. */
  dropAttr?: string;
}) {
  return (
    <div className={`${s.laneRow} ${side === 'bot' ? s.botRow : s.meRow}`}>
      <div className={`${s.laneTag} ${side === 'bot' ? s.tagEnemy : s.tagMe}`}>{label}</div>
      <div
        className={`${s.lane} ${isDropZone ? s.laneDropZone : ''} ${hoverDrop ? s.laneDropHover : ''}`}
        data-drop={dropAttr}
      >
        {units.map((u, i) => {
          if (!u) return (
            <div
              key={i}
              className={`${s.cell} ${s.cellEmpty} ${isDropZone ? s.cellValid : ''}`}
            />
          );
          const card = getCardData(u.id);
          if (!card) return <div key={i} className={`${s.cell} ${s.cellEmpty}`} />;
          const atk = u.atkOverride ?? card.atk ?? 0;
          const hp = u.hpOverride ?? card.hp ?? 0;
          return (
            <div
              key={i}
              className={`${s.cell} ${u.dying ? s.cellDying : ''}`}
              data-tut={`${side}-1-${i}`}
            >
              <button
                className={`${s.unit} ${canTap ? s.unitTargetable : ''} ${animOnReveal}`}
                onClick={canTap ? () => onTap(u) : undefined}
                disabled={!canTap}
              >
                {/* Арт юнита — fill всю карту; имя+статы поверх в gradient. */}
                <img
                  className={s.unitArt}
                  src={`/img/cards/${u.id}.jpg`}
                  alt=""
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
                <div className={s.unitName}>{card.name_ru}</div>
                <div className={s.unitStats}>
                  <span className={s.unitAtk}>⚔ {atk}</span>
                  <span className={s.unitHp}>♥ {hp}</span>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
